/**
 * One-tap ordering (docs/BACKLOG.md B18; owner, 2026-09-28: "make it quick and easy to order… click a button and get to
 * the page they need, because it's stored all in one place"). Every signal that says "we need more" (below par, ran out
 * in a check, thrown out, use soon) is merged and grouped by supplier. Each supplier card gets ONE big action (open its
 * saved order page, call it, or an Amazon search for non-food supplies) and "Send the list" (the phone's share sheet, a
 * text or an email to the supplier's contact the owner saved). CleanCleaver does NOT place supply orders and takes no
 * fee: it keeps the list, and the owner sends it to their OWN supplier. The status of a need is one of three, in words:
 * "On the list" (needed), "Sent to your supplier" (the owner sent or shared it), "Got it" (it arrived).
 *
 * Gates:
 * | G1 No invented numbers | qty = par − onHand only when both are known (≥ 1 when it ran out and par is known);
 *   par unknown → no number: the card asks the OWNER "How many?"; the sent text never carries a question |
 * | G2 Food never to Amazon | Amazon searches only for non-food supplies (cleaning, paper, chemical, equipment), from
 *   the product catalog (products.ts buyForSupply), shop.ts buySearches or the supply's own name; food and unknown items
 *   never get one. Every non-food item carries `buy` (the alternative to "Send the list"); the group's big action stays
 *   the supplier where there is one |
 * | G3 Nothing lost | a need with no supplier lands in the "No supplier set" group with a one-tap "Add the supplier" |
 * | G4 Memory lives in the app | an open "Sent" record shows "Sent today" on its items; an arrival (receiving check
 *   or a tap) closes it, adds what came to on-hand when on-hand is counted, and older signals stop asking again |
 */
import { RULE_SUPPLIES, type Supply, type SupplyKind } from "./actions.ts";
import type { Id, IsoDateTime } from "./model.ts";
import { counts, type Correction } from "./corrections.ts";
import { objectKind, objectState, type ActivityEvent, type PlaceObject } from "./objects.ts";
import { amazonSearchUrl, buySearches } from "./shop.ts";
import { buyForSupply, type BuyLink, type ProductOverride } from "./products.ts";
import type { SanitizerSetup } from "./sanitizer.ts";

/* ─────────────── model ─────────────── */

export type OrderWhy = "below_par" | "ran_out" | "thrown_out" | "use_soon";
export interface Supplier { name: string; phone?: string; email?: string; url?: string }
export type OrderActionKind = "open-url" | "call" | "amazon" | "add-supplier";
export interface OrderAction {
  kind: OrderActionKind;
  label: string;
  /** https:, tel: link (open-url, call, amazon). */
  href?: string;
  /** add-supplier: the supply to set the supplier on. */
  supplyKey?: string;
}

export interface OrderItem {
  /** Supply key, or `food:<name>` for a food with no supply row. */
  key: string;
  name: string;
  kind: SupplyKind;
  unit?: string;
  /** How many to order. Undefined when we can't know (G1): the card asks "How many?". */
  qty?: number;
  ask?: string;
  /** The main reason, and every reason. */
  why: OrderWhy;
  reasons: OrderWhy[];
  whyText: string;
  whereKept?: string;
  /** The storage spot it lives in (so a photo of that spot can show beside the place's name; placephoto.ts). */
  spotId?: string;
  /** Non-food only (G2). */
  amazon?: { label: string; href: string };
  /**
   * The one buy link beside "Send the list", as an alternative, on every non-food item (owner, 2026-09-30: "ability to buy
   * all items needed … from any screen from amazon"): the catalog purpose (products.ts), or the place's own product.
   */
  buy?: BuyLink;
  /** An open "Sent" record covers this item. */
  ordered?: { orderId: Id; at: IsoDateTime; by: string; text: string };
}

export interface OrderGroup {
  /** supplierKey(name), or NO_SUPPLIER. */
  key: string;
  title: string;
  supplier?: Supplier;
  items: OrderItem[];
  /** The one big button. */
  action: OrderAction;
  /** Folded extras (e.g. Call when the page is the main action). */
  more: OrderAction[];
  /** Copyable list of what isn't ordered yet: "Sysco — hand soap ×2, paper towels ×4". Empty when all are ordered. */
  text: string;
  /** Items not yet ordered. */
  toOrder: number;
  /** The newest open "Sent" record at this supplier (for "Got it"). */
  openOrderId?: Id;
}

export interface MaintenanceItem {
  objectId: Id;
  name: string;
  zone: string;
  state: "due" | "overdue" | "not_started" | "soon";
  dueAt?: IsoDateTime;
  whenText: string;
  vendor?: Supplier;
  action?: OrderAction;
}

export interface OrderList {
  groups: OrderGroup[];
  maintenance: MaintenanceItem[];
  /** Every supplier set on a supply or an object, once (pick one for another supply). */
  suppliers: (Supplier & { key: string })[];
  /** Nothing to order and nothing ordered. */
  empty: boolean;
}

/** A person's own "Sent" record (they sent the list to their supplier; we didn't order anything). Arrival is per line. */
export interface SupplierOrderLine {
  key: string;
  name: string;
  qty?: number;
  unit?: string;
  why?: OrderWhy;
  arrivedAt?: IsoDateTime;
  arrivedBy?: string;
  arrivedVia?: "receiving" | "tap";
}
export interface SupplierOrder {
  id: Id;
  orgId: Id;
  locationId: Id;
  supplierKey: string;
  supplierName?: string;
  lines: SupplierOrderLine[];
  orderedAt: IsoDateTime;
  orderedBy: string;
  /** Set when every line has arrived. */
  arrivedAt?: IsoDateTime;
  /** "Not sent after all" (corrections.ts): the record no longer counts; its items are back on the list. */
  correction?: Correction;
  /** "Not arrived after all": each one keeps the arrival it took back (the lines as they were), for the record. */
  arrivalCorrections?: ArrivalCorrection[];
}
export interface ArrivalCorrection extends Correction { lines: Pick<SupplierOrderLine, "key" | "name" | "qty" | "arrivedAt" | "arrivedBy" | "arrivedVia">[] }

export const NO_SUPPLIER = "none";
export const NO_SUPPLIER_TITLE = "No supplier set";
export const HOW_MANY = "How many?";
export const MAINTENANCE_DAYS = 14;
export const DISCARD_DAYS = 14;

/* ─────────────── small helpers ─────────────── */

/** "Sysco Foods " → "sysco-foods": one key per supplier name, whatever the spacing or case. */
export function supplierKey(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || NO_SUPPLIER;
}

const digits = (p: string) => p.replace(/[^\d+]/g, "");
const hasSupplier = (s?: Supplier): s is Supplier => !!s && !!s.name?.trim();
const isFood = (k: SupplyKind) => k === "food";
/** Words for matching a name to a supply or a receiving line: lowercase, letters and digits, no plural s. */
const words = (s: string) => s.toLowerCase().replace(/[^a-z0-9%]+/g, " ").trim().split(/\s+/).filter(Boolean).map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w));
/** `text` names `name`: the same words, or `name`'s words in a row inside `text` ("Hand soap refills" names "Hand soap"). */
export function namesItem(text: string, name: string): boolean {
  const t = words(text), n = words(name);
  if (!n.length || !t.length) return false;
  for (let i = 0; i + n.length <= t.length; i++) if (n.every((w, j) => t[i + j] === w)) return true;
  return false;
}
/** "Hand soap" → "hand soap"; keeps a name that starts with an acronym or brand capitals ("EPA disinfectant"). */
const listName = (n: string) => (/^[A-Z][a-z]/.test(n) ? n.charAt(0).toLowerCase() + n.slice(1) : n);

const localDay = (iso: IsoDateTime | Date, timeZone: string) => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(typeof iso === "string" ? new Date(iso) : iso);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
};
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
const weekday = (iso: IsoDateTime, timeZone: string) => new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long" }).format(new Date(iso));
const shortDate = (iso: IsoDateTime, timeZone: string) => new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric" }).format(new Date(iso));

/** The three words a need can have, in the order it moves. One model on web, phone and API (owner, 2026-09-29). */
export const ORDER_STATUS = { list: "On the list", sent: "Sent to your supplier", got: "Got it" } as const;
export type OrderStatusKey = keyof typeof ORDER_STATUS;

/** Where a "Sent" record stands: sent until every line has arrived, then got it. */
export function recordStatus(o: Pick<SupplierOrder, "arrivedAt" | "lines">): "sent" | "got" {
  return o.arrivedAt || (o.lines.length > 0 && o.lines.every((l) => l.arrivedAt)) ? "got" : "sent";
}

/** "Sent today", "Sent yesterday", "Sent Monday", "Sent Sep 12". */
export function sentText(at: IsoDateTime, now: Date | IsoDateTime, timeZone = "America/Denver"): string {
  const d = dayDiff(localDay(at, timeZone), localDay(now, timeZone));
  if (d <= 0) return "Sent today";
  if (d === 1) return "Sent yesterday";
  if (d < 7) return `Sent ${weekday(at, timeZone)}`;
  return `Sent ${shortDate(at, timeZone)}`;
}

/** An Amazon search for a non-food supply (G2): the specific search shop.ts makes from its name, else the name itself. */
export function amazonFor(s: Pick<Supply, "name" | "kind">, tag?: string): { label: string; href: string } | undefined {
  if (isFood(s.kind)) return undefined;
  const q = buySearches(s.name)[0]?.query ?? s.name;
  return { label: `Find ${listName(s.name)} on Amazon`, href: amazonSearchUrl(q, tag) };
}

/* ─────────────── the order list ─────────────── */

export interface OrderListInput {
  supplies: Supply[];
  /** Rules a check failed (e.g. rr_soap: no soap). With `at`, a failure older than the item's last arrival is ignored. */
  failedRuleIds?: (string | { ruleId: string; at?: IsoDateTime })[];
  /** Discard log lines and foods thrown out in the last 14 days. */
  discardsLast14Days?: { item: string; at?: IsoDateTime }[];
  /** core foodNotices (or the API's view of them: kind + name). Only throw_out and use_soon ask for more. */
  foodNotices?: ({ kind: string; name: string } | { kind: string; record: { name: string } })[];
  /** maintenanceDue(): shown as its own section. */
  maintenanceDue?: MaintenanceItem[];
  /** Recent "Sent" records at this place (open ones mark items; arrivals quiet older signals). */
  orders?: SupplierOrder[];
  /** Supply keys a person just said they're out of (the whole-place supplies roll-up, wholeplace.ts): ran out, like a failed check. */
  missing?: string[];
  now: Date | IsoDateTime;
  timeZone?: string;
  amazonTag?: string;
  /** The place's own products and sanitizer (products.ts productFor): the buy link follows them. */
  overrides?: readonly ProductOverride[] | null;
  /** The place's state: the rule text on a product is that state's (absent = Utah). */
  state?: string | null;
  sanitizer?: SanitizerSetup | null;
}

const WHY_RANK: Record<OrderWhy, number> = { ran_out: 0, below_par: 1, thrown_out: 2, use_soon: 3 };

function whyText(why: OrderWhy, s: { par?: number; onHand?: number }, fromCheck: boolean): string {
  switch (why) {
    case "ran_out": return fromCheck ? "Ran out in a check" : "None left";
    case "below_par": return `${s.onHand} left, keep ${s.par}`;
    case "thrown_out": return "Thrown out in the last 2 weeks";
    case "use_soon": return "Use it soon: order more";
  }
}

/** How many to order (G1). */
export function orderQty(s: Pick<Supply, "par" | "onHand">, ranOut: boolean): number | undefined {
  if (s.par === undefined) return undefined;
  if (ranOut) return Math.max(1, s.par - (s.onHand ?? 0));
  if (s.onHand !== undefined && s.onHand < s.par) return s.par - s.onHand;
  return undefined;
}

export function orderList(input: OrderListInput): OrderList {
  const tz = input.timeZone ?? "America/Denver";
  const supplies = input.supplies;
  const orders = (input.orders ?? []).filter(counts); // "Not sent after all" (corrections.ts)
  // Last arrival per item: a signal from before it is already answered (G4).
  const arrived = new Map<string, string>();
  for (const o of orders) for (const l of o.lines) if (l.arrivedAt && (!arrived.has(l.key) || l.arrivedAt > arrived.get(l.key)!)) arrived.set(l.key, l.arrivedAt);
  const fresh = (key: string, at?: string) => !at || !arrived.has(key) || at > arrived.get(key)!;

  // Signals, keyed by item.
  const reasons = new Map<string, Set<OrderWhy>>();
  const food = new Map<string, string>(); // food:<key> → display name, for food with no supply row
  const fromCheck = new Set<string>(); // ran out according to a failed check (not just a count of 0)
  const add = (key: string, why: OrderWhy) => { let r = reasons.get(key); if (!r) reasons.set(key, (r = new Set())); r.add(why); };
  const supplyFor = (name: string) => supplies.find((s) => namesItem(name, s.name) && namesItem(s.name, name)) ?? supplies.find((s) => s.kind === "food" && namesItem(name, s.name));
  const foodSignal = (name: string, why: OrderWhy, at?: string) => {
    const s = supplyFor(name);
    if (s) { if (fresh(s.key, at)) add(s.key, why); return; }
    const key = `food:${words(name).join("-")}`;
    if (key === "food:" || !fresh(key, at)) return;
    if (!food.has(key)) food.set(key, name.trim());
    add(key, why);
  };

  for (const f of input.failedRuleIds ?? []) {
    const { ruleId, at } = typeof f === "string" ? { ruleId: f, at: undefined } : f;
    for (const key of RULE_SUPPLIES[ruleId] ?? []) if (supplies.some((s) => s.key === key) && fresh(key, at)) { add(key, "ran_out"); fromCheck.add(key); }
  }
  for (const key of input.missing ?? []) if (supplies.some((s) => s.key === key)) { add(key, "ran_out"); fromCheck.add(key); }
  // "Out of it", said where the product is used (spots.ts): ran out, until a newer arrival answers it (G4).
  for (const s of supplies) if (s.stock?.state === "out" && fresh(s.key, s.stock.at)) add(s.key, "ran_out");
  // "Low" (the monthly stock check, spots.ts): order more, with no number unless par and on-hand are both known (G1).
  const saidLow = new Set<string>();
  for (const s of supplies) if (s.stock?.state === "low" && fresh(s.key, s.stock.at)) { add(s.key, "below_par"); saidLow.add(s.key); }
  for (const s of supplies) {
    if (s.par === undefined || s.onHand === undefined || s.onHand >= s.par) continue;
    add(s.key, s.onHand <= 0 ? "ran_out" : "below_par");
  }
  for (const d of input.discardsLast14Days ?? []) if (d.item?.trim()) foodSignal(d.item, "thrown_out", d.at);
  for (const n of input.foodNotices ?? []) {
    const name = "name" in n ? n.name : n.record.name;
    if (n.kind === "throw_out") foodSignal(name, "thrown_out");
    else if (n.kind === "use_soon") foodSignal(name, "use_soon");
  }

  // Open "Sent" records: newest per item.
  const open = new Map<string, { order: SupplierOrder; line: SupplierOrderLine }>();
  for (const o of [...orders].sort((a, b) => a.orderedAt.localeCompare(b.orderedAt))) for (const l of o.lines) if (!l.arrivedAt) open.set(l.key, { order: o, line: l });

  // Items.
  const items: { item: OrderItem; supplier?: Supplier }[] = [];
  const keys = new Set([...reasons.keys(), ...open.keys()]);
  for (const key of keys) {
    const s = supplies.find((x) => x.key === key);
    const r = [...(reasons.get(key) ?? [])].sort((a, b) => WHY_RANK[a] - WHY_RANK[b]);
    const o = open.get(key);
    const why: OrderWhy = r[0] ?? o?.line.why ?? "below_par";
    const kind: SupplyKind = s?.kind ?? "food";
    const name = s?.name ?? food.get(key) ?? o?.line.name ?? key;
    const qty = s ? orderQty(s, r.includes("ran_out")) : undefined;
    const item: OrderItem = {
      key, name, kind, ...(s?.unit ? { unit: s.unit } : {}),
      ...(o?.line.qty !== undefined ? { qty: o.line.qty } : qty !== undefined ? { qty } : { ask: HOW_MANY }),
      why, reasons: r, whyText: r.length ? (why === "below_par" && saidLow.has(key) && !(s?.par !== undefined && s.onHand !== undefined && s.onHand < s.par) ? "Running low" : whyText(why, s ?? {}, fromCheck.has(key))) : "Sent to your supplier: waiting for it",
      ...(s?.whereKept ? { whereKept: s.whereKept } : {}),
      ...(s?.spotId ? { spotId: s.spotId } : {}),
      ...(o ? { ordered: { orderId: o.order.id, at: o.order.orderedAt, by: o.order.orderedBy, text: sentText(o.order.orderedAt, input.now, tz) } } : {}),
    };
    // An open order was placed at a supplier: it stays on that supplier's card even if the supply's supplier changed.
    const supplier = hasSupplier(s?.supplier) ? s!.supplier : o && o.order.supplierKey !== NO_SUPPLIER ? { name: o.order.supplierName ?? o.order.supplierKey } : undefined;
    items.push({ item, supplier });
  }

  // Groups.
  const byKey = new Map<string, { supplier?: Supplier; items: OrderItem[] }>();
  for (const { item, supplier } of items) {
    const k = supplier ? supplierKey(supplier.name) : NO_SUPPLIER;
    let g = byKey.get(k);
    if (!g) byKey.set(k, (g = { supplier: supplier ? { ...supplier } : undefined, items: [] }));
    // Fill in contact details another supply of the same supplier has.
    if (g.supplier && supplier) { g.supplier.url ??= supplier.url; g.supplier.phone ??= supplier.phone; g.supplier.email ??= supplier.email; }
    g.items.push(item);
  }
  const groups: OrderGroup[] = [];
  for (const [key, g] of byKey) {
    g.items.sort((a, b) => Number(!!a.ordered) - Number(!!b.ordered) || WHY_RANK[a.why] - WHY_RANK[b.why] || a.name.localeCompare(b.name));
    const title = g.supplier?.name.trim() ?? NO_SUPPLIER_TITLE;
    const nonFood = g.items.filter((i) => !isFood(i.kind));
    const allNonFood = nonFood.length === g.items.length;
    // Amazon only where there is no supplier to reach (G2 keeps food out entirely).
    const reachable = !!g.supplier?.url || !!g.supplier?.phone;
    if (!reachable) for (const i of nonFood) { const a = amazonFor(i, input.amazonTag); if (a) i.amazon = a; }
    // Every non-food item also gets its one buy link (the alternative to sending the list), never food (G2).
    for (const i of nonFood) { const b = buyForSupply(i, { overrides: input.overrides, sanitizer: input.sanitizer, amazonTag: input.amazonTag, state: input.state }); if (b) i.buy = b; }
    let action: OrderAction;
    const more: OrderAction[] = [];
    if (g.supplier?.url) {
      action = { kind: "open-url", label: `Open ${title}`, href: g.supplier.url };
      if (g.supplier.phone) more.push({ kind: "call", label: `Call ${title}`, href: `tel:${digits(g.supplier.phone)}` });
    } else if (g.supplier?.phone) {
      action = { kind: "call", label: `Call ${title}`, href: `tel:${digits(g.supplier.phone)}` };
    } else if (g.supplier && allNonFood && g.items[0]?.amazon) {
      action = { kind: "amazon", label: "Find on Amazon", href: g.items[0].amazon.href };
      more.push({ kind: "add-supplier", label: `Add ${title}'s order page`, supplyKey: g.items[0].key });
    } else if (g.supplier) {
      action = { kind: "add-supplier", label: `Add ${title}'s order page`, supplyKey: g.items[0]!.key };
    } else {
      action = { kind: "add-supplier", label: "Add the supplier", supplyKey: g.items[0]!.key };
    }
    const todo = g.items.filter((i) => !i.ordered);
    const text = todo.length ? `${title} — ${todo.map((i) => `${listName(i.name)}${i.qty !== undefined ? ` ×${i.qty}${i.unit ? ` (${i.unit})` : ""}` : ""}`).join(", ")}` : "";
    const openIds = g.items.filter((i) => i.ordered).sort((a, b) => b.ordered!.at.localeCompare(a.ordered!.at));
    groups.push({ key, title, ...(g.supplier ? { supplier: g.supplier } : {}), items: g.items, action, more, text, toOrder: todo.length, ...(openIds[0] ? { openOrderId: openIds[0].ordered!.orderId } : {}) });
  }
  groups.sort((a, b) => Number(a.key === NO_SUPPLIER) - Number(b.key === NO_SUPPLIER) || Number(b.toOrder > 0) - Number(a.toOrder > 0) || a.title.localeCompare(b.title));

  const maintenance = [...(input.maintenanceDue ?? [])];
  return { groups, maintenance, suppliers: knownSuppliers(supplies, maintenance.map((m) => m.vendor)), empty: groups.length === 0 && maintenance.length === 0 };
}

/** Every supplier set anywhere, once per name, with the most contact details any row gave it. */
export function knownSuppliers(supplies: Supply[], more: (Supplier | undefined)[] = []): (Supplier & { key: string })[] {
  const out = new Map<string, Supplier & { key: string }>();
  for (const s of [...supplies.map((x) => x.supplier), ...more]) {
    if (!hasSupplier(s)) continue;
    const key = supplierKey(s.name);
    const cur = out.get(key);
    if (!cur) out.set(key, { key, name: s.name.trim(), ...(s.phone ? { phone: s.phone } : {}), ...(s.email ? { email: s.email } : {}), ...(s.url ? { url: s.url } : {}) });
    else { if (!cur.phone && s.phone) cur.phone = s.phone; if (!cur.email && s.email) cur.email = s.email; if (!cur.url && s.url) cur.url = s.url; }
  }
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/* ─────────────── maintenance due ─────────────── */

/**
 * Upkeep (descaling, hood filters, grease trap: objectKind "maintain") due within `days`, or overdue, or never done.
 * The vendor is the object's own supplier, when one is set.
 */
export function maintenanceDue(objects: PlaceObject[], events: Pick<ActivityEvent, "objectId" | "kind" | "at">[], now: Date | IsoDateTime, opts: { days?: number; timeZone?: string } = {}): MaintenanceItem[] {
  const days = opts.days ?? MAINTENANCE_DAYS;
  const tz = opts.timeZone ?? "America/Denver";
  const nowMs = typeof now === "string" ? Date.parse(now) : now.getTime();
  const out: MaintenanceItem[] = [];
  for (const o of objects) {
    if (objectKind(o) !== "maintain") continue;
    const st = objectState(o, { events, openReports: [], supplies: [], now });
    let state: MaintenanceItem["state"], whenText: string;
    if (st.state === "not_started") { state = "not_started"; whenText = "Not done here yet"; }
    else if (st.state === "overdue") { state = "overdue"; whenText = `Overdue since ${shortDate(st.dueAt!, tz)}`; }
    else if (st.state === "due") { state = "due"; whenText = "Due now"; }
    else if (st.dueAt && Date.parse(st.dueAt) - nowMs <= days * 86_400_000) {
      const d = dayDiff(localDay(new Date(nowMs), tz), localDay(st.dueAt, tz));
      state = "soon"; whenText = d <= 0 ? "Due today" : d === 1 ? "Due tomorrow" : `Due in ${d} days`;
    } else continue;
    const v = hasSupplier(o.supplier) ? o.supplier : undefined;
    const action: OrderAction | undefined = v?.phone ? { kind: "call", label: `Call ${v.name}`, href: `tel:${digits(v.phone)}` } : v?.url ? { kind: "open-url", label: `Open ${v.name}`, href: v.url } : undefined;
    out.push({ objectId: o.id, name: o.count && o.count > 1 ? `${o.name} ×${o.count}` : o.name, zone: o.zone, state, ...(st.dueAt ? { dueAt: st.dueAt } : {}), whenText, ...(v ? { vendor: v } : {}), ...(action ? { action } : {}) });
  }
  const rank = { overdue: 0, due: 1, not_started: 2, soon: 3 } as const;
  return out.sort((a, b) => rank[a.state] - rank[b.state] || (a.dueAt ?? "").localeCompare(b.dueAt ?? "") || a.name.localeCompare(b.name));
}

/* ─────────────── "Sent" and "Got it" ─────────────── */

export class OrderingError extends Error { constructor(message: string) { super(message); this.name = "OrderingError"; } }

/** The record "Mark it sent" writes. Lines name what was ordered; a qty is kept only when the person or the list had one. */
export function orderedRecord(o: { id: Id; orgId: Id; locationId: Id; supplierKey: string; supplierName?: string; lines: SupplierOrderLine[]; by: string; now: IsoDateTime }): SupplierOrder {
  const lines = o.lines.filter((l) => l.key.trim());
  if (!lines.length) throw new OrderingError("Pick at least one thing you sent.");
  const seen = new Set<string>();
  const clean = lines.filter((l) => !seen.has(l.key) && seen.add(l.key)).map((l) => ({ key: l.key, name: l.name, ...(l.qty !== undefined ? { qty: l.qty } : {}), ...(l.unit ? { unit: l.unit } : {}), ...(l.why ? { why: l.why } : {}) }));
  return { id: o.id, orgId: o.orgId, locationId: o.locationId, supplierKey: o.supplierKey, ...(o.supplierName ? { supplierName: o.supplierName } : {}), lines: clean, orderedAt: o.now, orderedBy: o.by };
}

/** Mark lines arrived (all, or the named keys). `arrived` lists the lines that changed. */
export function markArrived(order: SupplierOrder, a: { keys?: string[]; by: string; at: IsoDateTime; via: "receiving" | "tap" }): { order: SupplierOrder; arrived: SupplierOrderLine[] } {
  const arrived: SupplierOrderLine[] = [];
  const lines = order.lines.map((l) => {
    if (l.arrivedAt || (a.keys && !a.keys.includes(l.key))) return l;
    const n = { ...l, arrivedAt: a.at, arrivedBy: a.by, arrivedVia: a.via };
    arrived.push(n);
    return n;
  });
  const all = lines.every((l) => l.arrivedAt);
  return { order: { ...order, lines, ...(all ? { arrivedAt: order.arrivedAt ?? a.at } : {}) }, arrived };
}

/**
 * A receiving check's rows: each accepted line that names an item on an open order marks it arrived. A rejected line
 * doesn't (the delivery was refused).
 */
export function receivingArrivals(orders: SupplierOrder[], rows: { item?: unknown; result?: unknown }[], a: { by: string; at: IsoDateTime }): { order: SupplierOrder; arrived: SupplierOrderLine[] }[] {
  const names = rows.filter((r) => typeof r.item === "string" && r.item.trim() && r.result !== "reject").map((r) => r.item as string);
  if (!names.length) return [];
  const out: { order: SupplierOrder; arrived: SupplierOrderLine[] }[] = [];
  for (const o of orders) {
    if (o.arrivedAt) continue;
    const keys = o.lines.filter((l) => !l.arrivedAt && names.some((n) => namesItem(n, l.name))).map((l) => l.key);
    if (!keys.length) continue;
    const r = markArrived(o, { keys, by: a.by, at: a.at, via: "receiving" });
    if (r.arrived.length) out.push(r);
  }
  return out;
}

/** On-hand after an arrival: adds what came, only where on-hand is counted and the qty is known (never invented). */
export function stockAfterArrival(supplies: Supply[], arrived: SupplierOrderLine[]): { supplies: Supply[]; changed: boolean } {
  let changed = false;
  const next = supplies.map((s) => {
    const add = arrived.filter((l) => l.key === s.key && l.qty !== undefined).reduce((n, l) => n + l.qty!, 0);
    if (!add || s.onHand === undefined) return s;
    changed = true;
    return { ...s, onHand: s.onHand + add };
  });
  return { supplies: next, changed };
}

/** Taking an arrival back (Undo or "Not arrived after all"): the on-hand counts it added come off again, never below 0. */
export function stockAfterUnarrival(supplies: Supply[], lines: Pick<SupplierOrderLine, "key" | "qty">[]): { supplies: Supply[]; changed: boolean } {
  let changed = false;
  const next = supplies.map((s) => {
    const sub = lines.filter((l) => l.key === s.key && l.qty !== undefined).reduce((n, l) => n + l.qty!, 0);
    if (!sub || s.onHand === undefined) return s;
    changed = true;
    return { ...s, onHand: Math.max(0, s.onHand - sub) };
  });
  return { supplies: next, changed };
}

/** The last arrival on an order: the lines marked arrived at the latest moment, by whom. None → undefined. */
export function lastArrival(order: SupplierOrder): { at: IsoDateTime; by: string; lines: SupplierOrderLine[] } | undefined {
  const arrived = order.lines.filter((l) => l.arrivedAt);
  if (!arrived.length) return undefined;
  const at = arrived.reduce((m, l) => (l.arrivedAt! > m ? l.arrivedAt! : m), "");
  const lines = arrived.filter((l) => l.arrivedAt === at);
  return { at, by: lines[0]!.arrivedBy ?? "", lines };
}

/** The order with its last arrival taken back (open again). `correction`: kept on the order with those lines, for the record. */
export function takeBackArrival(order: SupplierOrder, correction?: Correction): { order: SupplierOrder; lines: SupplierOrderLine[] } {
  const last = lastArrival(order);
  if (!last) return { order, lines: [] };
  const keys = new Set(last.lines.map((l) => l.key));
  const lines = order.lines.map((l) => (keys.has(l.key) && l.arrivedAt === last.at ? { key: l.key, name: l.name, ...(l.qty !== undefined ? { qty: l.qty } : {}), ...(l.unit ? { unit: l.unit } : {}), ...(l.why ? { why: l.why } : {}) } : l));
  const { arrivedAt: _gone, ...rest } = order;
  const kept: ArrivalCorrection[] = correction
    ? [...(order.arrivalCorrections ?? []), { ...correction, lines: last.lines.map((l) => ({ key: l.key, name: l.name, ...(l.qty !== undefined ? { qty: l.qty } : {}), arrivedAt: l.arrivedAt, arrivedBy: l.arrivedBy, arrivedVia: l.arrivedVia })) }]
    : order.arrivalCorrections ?? [];
  return { order: { ...rest, lines, ...(kept.length ? { arrivalCorrections: kept } : {}) }, lines: last.lines };
}

/** Set (or clear) one supply's supplier. Unknown key → the supply list unchanged and `found: false`. */
export function withSupplier(supplies: Supply[], key: string, supplier: Supplier | null): { supplies: Supply[]; found: boolean } {
  let found = false;
  const next = supplies.map((s) => {
    if (s.key !== key) return s;
    found = true;
    const { supplier: _old, ...rest } = s;
    return supplier ? { ...rest, supplier: { name: supplier.name.trim(), ...(supplier.phone ? { phone: supplier.phone } : {}), ...(supplier.email ? { email: supplier.email } : {}), ...(supplier.url ? { url: supplier.url } : {}) } } : rest;
  });
  return { supplies: next, found };
}

/** The words of the one-time note (migration 0045), the same on web and phone. */
export const ORDER_NOTE_TEXT = "An earlier version marked some items 'placed' without ordering anything. We put them back on your list.";

/** The note a manager sees on Order: only managers, only until someone dismisses it. */
export function orderNoteFor(loc: { orderNote?: { names: string[]; dismissedAt?: string } }, canOrder: boolean): { text: string; names: string[] } | undefined {
  const n = loc.orderNote;
  return canOrder && n && !n.dismissedAt ? { text: ORDER_NOTE_TEXT, names: n.names } : undefined;
}

/* ─────────────── for the apps: what "Send the list" sends ─────────────── */

/** What is on the list right now (not yet sent), for the For You card and the Today line. */
export interface OrderNeeds { count: number; names: string[] }
export function orderNeeds(list: Pick<OrderList, "groups">): OrderNeeds {
  const names = list.groups.flatMap((g) => g.items.filter((i) => !i.ordered).map((i) => listName(i.name)));
  return { count: names.length, names };
}

/**
 * The plain text "Send the list" hands to the share sheet, a text or an email. It is the owner's message to their OWN
 * supplier (we never send it): who it is from, then one line per item with its number, and a thank-you.
 *   Hi Wasatch Supply, this is Blue Door Coffee. We need:
 *   - hand soap x2 (case of 4)
 *   - paper towels      (no number yet: the line is just the name; we never write a question to the supplier)
 *   Thank you.
 */
export function sendListText(g: Pick<OrderGroup, "title" | "items" | "supplier">, business: string, typed: Record<string, number | undefined> = {}): string {
  const lines = orderedLinesFor(g, typed);
  if (!lines.length) return "";
  const to = g.supplier?.name.trim();
  const from = business.trim();
  const head = `${to ? `Hi ${to}, ` : "Hi, "}${from ? `this is ${from}` : "this is our order"}. We need:`;
  const rows = lines.map((l) => { const unit = g.items.find((i) => i.key === l.key)?.unit; return `- ${listName(l.name)}${l.qty !== undefined ? ` x${l.qty}${unit ? ` (${unit})` : ""}` : ""}`; });
  return [head, ...rows, "Thank you."].join("\n");
}

/** A text or an email the owner sends from their own phone or mail app, aimed at the supplier contact they saved. */
export function sendLinks(supplier: Pick<Supplier, "name" | "phone" | "email"> | undefined, text: string, business: string): { label: string; kind: "sms" | "mailto"; href: string }[] {
  if (!supplier || !text) return [];
  const out: { label: string; kind: "sms" | "mailto"; href: string }[] = [];
  if (supplier.phone && digits(supplier.phone).replace("+", "").length >= 7) out.push({ label: `Text ${supplier.name}`, kind: "sms", href: `sms:${digits(supplier.phone)}?&body=${encodeURIComponent(text)}` });
  if (supplier.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplier.email.trim())) out.push({ label: `Email ${supplier.name}`, kind: "mailto", href: `mailto:${supplier.email.trim()}?subject=${encodeURIComponent(business.trim() ? `Order list from ${business.trim()}` : "Order list")}&body=${encodeURIComponent(text)}` });
  return out;
}

/** The lines "Mark it sent" records for a card: every item not yet ordered, with the number the person typed, else the list's. */
export function orderedLinesFor(g: Pick<OrderGroup, "items">, typed: Record<string, number | undefined> = {}): { key: string; name: string; qty?: number; why: OrderWhy }[] {
  return g.items.filter((i) => !i.ordered).map((i) => {
    const t = typed[i.key];
    const qty = t !== undefined && Number.isInteger(t) && t > 0 ? t : i.qty;
    return { key: i.key, name: i.name, ...(qty !== undefined ? { qty } : {}), why: i.why };
  });
}

/** The short list with the numbers the person typed ("Sysco — hand soap ×3, paper towels"). */
export function groupText(g: Pick<OrderGroup, "title" | "items">, typed: Record<string, number | undefined> = {}): string {
  const lines = orderedLinesFor(g, typed);
  if (!lines.length) return "";
  return `${g.title} — ${lines.map((l) => { const unit = g.items.find((i) => i.key === l.key)?.unit; return `${listName(l.name)}${l.qty !== undefined ? ` ×${l.qty}${unit ? ` (${unit})` : ""}` : ""}`; }).join(", ")}`;
}

/* ─────────────── asking the owner "How many?" (never the supplier) ─────────────── */

/** Items on a card that have no number yet (no typed amount, no computed qty): the owner is asked before Send. */
export function blankAmounts(g: Pick<OrderGroup, "items">, typed: Record<string, number | undefined> = {}): OrderItem[] {
  return g.items.filter((i) => !i.ordered && orderedLinesFor({ items: [i] }, typed)[0]?.qty === undefined);
}

/** "No amount yet for dish soap, gloves. Add how many so Sysco knows." Empty when every line has a number. */
export function blankAmountsPrompt(g: Pick<OrderGroup, "items" | "supplier">, typed: Record<string, number | undefined> = {}): string {
  const b = blankAmounts(g, typed);
  if (!b.length) return "";
  const names = b.map((i) => listName(i.name));
  const who = g.supplier?.name.trim() || "your supplier";
  return `No amount yet for ${names.length > 2 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names.join(" and ")}. Add how many so ${who} knows.`;
}

/** "Use 1 of each I left blank": the person's explicit choice. Fills ONLY the blanks; typed and computed numbers stay. */
export function fillBlanksWithOne(g: Pick<OrderGroup, "items">, typed: Record<string, number | undefined> = {}): Record<string, number | undefined> {
  const next = { ...typed };
  for (const i of blankAmounts(g, typed)) next[i.key] = 1;
  return next;
}

/** What the person typed last time, per supply key, kept as its usual order amount. */
export type LastAmounts = Record<string, number>;

/** Merge the amounts just sent (only numbers the person typed) into the remembered usual amounts. */
export function rememberAmounts(last: LastAmounts, typed: Record<string, number | undefined>, keys: string[]): LastAmounts {
  const next = { ...last };
  for (const k of keys) { const t = typed[k]; if (t !== undefined && Number.isInteger(t) && t > 0) next[k] = t; }
  return next;
}

/** "Last time: 2" for an item with no number of its own, else undefined. */
export function lastTimeText(item: Pick<OrderItem, "key" | "qty">, last: LastAmounts): string | undefined {
  const n = last[item.key];
  return item.qty === undefined && n !== undefined ? `Last time: ${n}` : undefined;
}

/** Prefill: the remembered amount for each blank item the person has not typed yet. Never overwrites a typed number. */
export function prefillFromLast(g: Pick<OrderGroup, "items">, typed: Record<string, number | undefined>, last: LastAmounts): Record<string, number | undefined> {
  const next = { ...typed };
  for (const i of g.items) if (!i.ordered && i.qty === undefined && next[i.key] === undefined && last[i.key] !== undefined) next[i.key] = last[i.key];
  return next;
}

/** Items sent with a number but no "Keep at least" (par) yet: offered once so future lists compute amounts (G1). */
export function offerKeepAtLeast(g: Pick<OrderGroup, "items">, typed: Record<string, number | undefined>, supplies: Pick<Supply, "key" | "par">[]): OrderItem[] {
  return g.items.filter((i) => !i.ordered && !i.key.startsWith("food:") && supplies.find((s) => s.key === i.key)?.par === undefined && (typed[i.key] ?? 0) > 0);
}

/** The stepper's one tap: blank + 1 = 1, and 1 − 1 = blank again (never 0, never negative). */
export function stepAmount(n: number | undefined, by: 1 | -1): number | undefined {
  const next = (n ?? 0) + by;
  return next >= 1 ? Math.min(10000, next) : undefined;
}
